import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { OidcAuthService } from './oidc-auth.service';
import { SubscriptionService } from './subscription.service';

@Component({
  selector: 'app-subscription-prompt',
  standalone: true,
  imports: [CommonModule],
  template: `
  <div class="sub-wrap" [class.mobile]="mobile">
    <div class="sub-card">
      <div class="brandline"></div>

      <div class="head">
        <div class="logo"><img src="assets/logos/cypress.svg" alt="" /></div>
        <h1>Subscribe to continue</h1>
        <p class="sub" *ngIf="userEmail">Signed in as {{ userEmail }}</p>
      </div>

      <div class="plan">
        <div class="plan__top">
          <div class="plan__name">{{ planName }}</div>
        </div>
        <div class="plan__price">{{ planPrice }}<span class="per">{{ planPeriod }}</span></div>
        <div class="beta-note">🔥 Early-access pricing, limited time — lock in $99/year before it goes up. Limited early-user spots, and your rate stays locked in.</div>
        <ul class="feats">
          <li *ngFor="let f of features">{{ f }}</li>
        </ul>
        <div class="demo-row">
          <a class="demo-link" href="assets/demo/index.html" target="baja-demo"
             (click)="openDemo($event, 'index.html')">Scientists</a>
          <a class="demo-link demo-link--alt" href="assets/demo/for-you.html" target="baja-demo-curious"
             (click)="openDemo($event, 'for-you.html')">Non-scientists</a>
        </div>
      </div>

      <div class="notice" *ngIf="checking">Checking your subscription…</div>
      <div class="notice ok" *ngIf="justSubscribed">✅ You're subscribed — taking you in…</div>
      <div class="notice warn" *ngIf="canceled">Checkout canceled. You can subscribe whenever you're ready.</div>
      <div class="error" *ngIf="error">{{ error }}</div>

      <button class="cta" [disabled]="busy || checking" (click)="subscribe()">
        <span *ngIf="!busy">Subscribe</span>
        <span *ngIf="busy">Redirecting…</span>
      </button>
      <div class="pay-note">Card, Apple&nbsp;Pay, Google&nbsp;Pay &amp; Link · secured by Stripe</div>

      <!-- Free tier. Someone who is not ready to pay should have a way in rather than a dead
           end: this page previously offered only Subscribe, Contact-us and Sign-out. -->
      <div class="or-row"><span>or</span></div>
      <button class="free-btn" type="button" (click)="useFree()">
        Continue with the free version
      </button>
      <div class="free-note">
        Full editor — load, edit, save and design without limit.
        {{ freeLimit }} AI requests and {{ freeLimit }} off-target searches per month.
      </div>

      <a class="enterprise-btn" href="mailto:contact@baja.bio?subject=Baja.bio%20enterprise%20inquiry">
        ✉ Contact us for enterprise use
      </a>

      <button class="ghost" type="button" (click)="signOut()">Sign out</button>
    </div>
  </div>
  `,
  styles: [`

    /* ---- Phone: the plan, the price and the buttons. Feature lists, demo links, the
       early-access note and the small print are hidden so the page fits one screen. ---- */
    .sub-wrap.mobile { padding: 12px; min-height: 100vh; align-items: flex-start; }
    .sub-wrap.mobile .sub-card { padding: 16px 14px; width: 100%; max-width: 420px; margin-top: 6vh; }
    .sub-wrap.mobile .beta-note, .sub-wrap.mobile .feats, .sub-wrap.mobile .demo-row,
    .sub-wrap.mobile .pay-note, .sub-wrap.mobile .free-note, .sub-wrap.mobile .enterprise-btn { display: none !important; }
    .sub-wrap.mobile .head h1 { font-size: 18px; margin: 6px 0 2px; }
    .sub-wrap.mobile .logo img { width: 48px; height: 48px; }
    .sub-wrap.mobile .plan { padding: 12px; margin: 8px 0; }
    .sub-wrap.mobile .cta, .sub-wrap.mobile .free-btn { padding: 12px 14px; }
    .sub-wrap.mobile .or-row { margin: 8px 0; }
    :host { display:block; width:100%; height:100%; }
    .sub-wrap {
      position:relative; overflow:hidden; min-height:100vh;
      display:flex; align-items:center; justify-content:center; padding:24px; box-sizing:border-box;
      background:
        radial-gradient(1200px 600px at 15% -10%, rgba(22,196,127,0.25), transparent 60%),
        radial-gradient(1000px 600px at 110% 10%, rgba(18,167,232,0.28), transparent 55%),
        radial-gradient(900px 700px at 50% 120%, rgba(255,140,26,0.20), transparent 55%),
        #071b2a;
      font-family:"Segoe UI", system-ui, -apple-system, Roboto, Arial, sans-serif;
    }
    .sub-card {
      position:relative; width:100%; max-width:420px; overflow:hidden;
      background: rgba(10,25,40,0.74); backdrop-filter: blur(14px);
      border:1px solid rgba(18,194,224,0.28); border-radius:18px; padding:30px 28px 22px;
      box-shadow: 0 24px 70px rgba(0,0,0,0.45);
    }
    .brandline { position:absolute; top:0; left:0; right:0; height:5px;
      background: linear-gradient(90deg,#16c47f,#12c2c2,#12a7e8,#ff8c1a); }
    .head { text-align:center; margin-bottom:18px; }
    .logo { width:64px; height:64px; margin:6px auto 12px; border-radius:16px; overflow:hidden;
      display:flex; align-items:center; justify-content:center; background:#fff; border:1px solid rgba(18,194,224,0.35); }
    .logo img { width:100%; height:100%; object-fit:contain; padding:7px; box-sizing:border-box; }
    h1 { margin:0; font-size:22px; font-weight:700; color:#eaf6f9; }
    .sub { margin:6px 0 0; font-size:13px; color:#9db6c4; }
    .plan { border:1px solid rgba(18,194,224,0.22); border-radius:14px; padding:16px 18px; margin-bottom:16px;
      background: rgba(18,194,224,0.06); }
    .plan__top { display:flex; align-items:center; justify-content:space-between; gap:8px; }
    .plan__name { font-size:14px; font-weight:800; letter-spacing:.2px; color:#12c2e0; }
    .beta-note { margin-top:10px; padding:8px 10px; border-radius:9px; font-size:12.5px; line-height:1.45;
      color:#ffe0c2; background: rgba(255,140,26,0.12); border:1px solid rgba(255,140,26,0.4); }
    .demo-row { display:flex; flex-wrap:nowrap; gap:10px; margin-top:14px; }
    /* sunset beach: warm gold -> orange -> coral */
    .demo-link { display:inline-block; font-size:13px; font-weight:800; cursor:pointer;
      color:#3a1500; text-decoration:none; background: linear-gradient(135deg,#ffd166,#ff9a3c,#ff6b81);
      border-radius:10px; padding:10px 14px; box-shadow:0 6px 16px rgba(255,120,80,0.38);
      transition: transform .1s ease, filter .15s ease; flex:1 1 0; text-align:center; }
    .demo-link--alt { color:#4a1224; background: linear-gradient(135deg,#ffb15a,#ff7a8a,#ff5e8a); }
    .demo-link:hover { filter:brightness(1.06); transform: translateY(-1px); }
    .plan__price { font-size:34px; font-weight:800; color:#eaf6f9; margin-top:4px; }
    .plan__price .per { font-size:14px; font-weight:500; color:#9db6c4; margin-left:4px; }
    .feats { list-style:none; margin:12px 0 0; padding:0; }
    .feats li { position:relative; padding:5px 0 5px 22px; color:#cfe4ec; font-size:13.5px; }
    .feats li::before { content:"✓"; position:absolute; left:0; color:#16c47f; font-weight:800; }
    .cta { width:100%; margin-top:4px; padding:13px 16px; border:none; border-radius:12px; cursor:pointer;
      font-size:15px; font-weight:800; color:#3a1500;
      background: linear-gradient(135deg,#ffca28,#ff8c42,#ff6b81); box-shadow:0 8px 22px rgba(255,120,80,0.45);
      transition: transform .1s ease, box-shadow .18s ease, opacity .15s ease; }
    .cta:hover:not(:disabled) { transform: translateY(-1px); box-shadow:0 10px 26px rgba(255,120,80,0.6); }
    .cta:disabled { opacity:.6; cursor:default; }
    .pay-note { margin-top:10px; text-align:center; font-size:11.5px; color:#7e97a6; }
    .or-row { display:flex; align-items:center; gap:10px; margin:16px 0 10px; color:#7f97a6; font-size:12px; }
    .or-row:before, .or-row:after { content:""; flex:1 1 auto; height:1px; background:rgba(255,255,255,0.14); }
    .free-btn { display:block; width:100%; box-sizing:border-box; padding:12px 16px; cursor:pointer;
      text-align:center; font-size:14px; font-weight:800; color:#eaf6f9;
      background: rgba(255,255,255,0.06); border:1px solid rgba(255,255,255,0.28); border-radius:12px;
      transition: transform .1s ease, background .15s ease; }
    .free-btn:hover { transform: translateY(-1px); background: rgba(255,255,255,0.12); }
    .free-note { margin-top:8px; font-size:12px; line-height:1.55; color:#8fb8c8; text-align:center; }
    .enterprise-btn { display:block; width:100%; box-sizing:border-box; margin-top:12px; padding:10px 14px;
      text-align:center; text-decoration:none; font-size:13px; font-weight:800; color:#bfeef6;
      background: rgba(18,194,224,0.10); border:1px solid rgba(18,194,224,0.45); border-radius:11px;
      transition: transform .1s ease, background .15s ease, box-shadow .15s ease; }
    .enterprise-btn:hover { transform: translateY(-1px); background: rgba(18,194,224,0.18);
      box-shadow:0 8px 20px rgba(18,167,232,0.25); }
    .notice { margin:2px 0 14px; padding:9px 12px; border-radius:9px; font-size:13px; color:#bcd3df;
      background: rgba(18,194,224,0.1); border:1px solid rgba(18,194,224,0.25); }
    .notice.ok { color:#c8f7e2; background: rgba(22,196,127,0.14); border-color: rgba(22,196,127,0.4); }
    .notice.warn { color:#ffe0c2; background: rgba(255,140,26,0.12); border-color: rgba(255,140,26,0.35); }
    .error { margin:2px 0 14px; padding:9px 12px; border-radius:9px; font-size:13px;
      color:#ffd9c2; background: rgba(255,90,40,0.14); border:1px solid rgba(255,140,26,0.4); }
    .ghost { display:block; margin:16px auto 0; background:none; border:none; cursor:pointer;
      font-size:12.5px; color:#7e97a6; text-decoration:underline; }
    .ghost:hover { color:#bcd3df; }
  `],
})
export class SubscriptionPromptComponent implements OnInit {

  // A phone (or an iPad reporting a desktop UA but with touch): the page is trimmed to what
  // fits on a small screen. Same rule as the lionscript isMobile().
  mobile: boolean = (() => {
    try {
      const ua = navigator.userAgent || '';
      if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|Windows Phone|Mobile/i.test(ua)) return true;
      if (/Macintosh/i.test(ua) && (navigator.maxTouchPoints || 0) > 1) return true;
      return Math.min(window.innerWidth || 9999, window.innerHeight || 9999) <= 480;
    } catch (e) { return false; }
  })();
  userEmail: string | null = null;
  busy = false;
  checking = true;
  justSubscribed = false;
  /** Free-tier monthly allowance, shown on the free option. Matches FREE_LIMIT in baja-server. */
  freeLimit = 5;
  canceled = false;
  error = '';

  // Plan copy. The price/period are fetched live from Stripe in ngOnInit (so the UI always
  // matches STRIPE_PRICE_ID); the env values are only a fallback if that fetch fails.
  planName = String((window as any)['env']?.['planName'] || 'Baja.Bio');
  planPrice = String((window as any)['env']?.['planPrice'] || '');
  planPeriod = String((window as any)['env']?.['planPeriod'] || '');
  features: string[] = ((window as any)['env']?.['planFeatures']) || [
    'Full genome editor & multi-track browser',
    'Design siRNA, ASO & qPCR assays',
    'Off-target analysis & splicing models',
    'Patent database, RNA-binding proteins & RNA-seq layers',
    'Bonus book: "The Chemistry of RNA Therapeutics"',
  ];

  constructor(
    private auth: OidcAuthService,
    private sub: SubscriptionService,
    private router: Router,
  ) {}

  async ngOnInit() {
    this.userEmail = this.auth.getUser()?.email || null;

    // Show the real Stripe price (falls back to env / a sane default if unavailable).
    this.sub.priceInfo().then(p => {
      if (p && p.display) { this.planPrice = p.display; this.planPeriod = p.period || ''; }
      else if (!this.planPrice) { this.planPrice = '$49'; this.planPeriod = '/month'; }
    });

    const params = new URLSearchParams(window.location.search);
    const status = params.get('status');
    this.canceled = status === 'cancel';

    // Returning from Checkout: give Stripe a moment, then confirm and route in.
    if (status === 'success') {
      this.sub.clearCache();
      await this.pollActive();
      return;
    }

    // Already subscribed? go straight in.
    const s = await this.sub.status();
    this.checking = false;
    if (s.active) this.goIn();
  }

  private async pollActive(tries = 8): Promise<void> {
    this.checking = true;
    for (let i = 0; i < tries; i++) {
      const s = await this.sub.status();
      if (s.active) { this.checking = false; this.justSubscribed = true; setTimeout(() => this.goIn(), 900); return; }
      await new Promise(r => setTimeout(r, 1200));
    }
    this.checking = false; // webhook not synced yet — let them retry
  }

  private goIn() {
    const dest = sessionStorage.getItem('oidc.returnTo') || '/';
    sessionStorage.removeItem('oidc.returnTo');
    // Full page LOAD (not an Angular SPA route change) so the app bootstraps via
    // window.env.init (/app/baja/init.js). A client-side navigateByUrl leaves the
    // engine un-initialized and the "taking you in…" screen stalls.
    window.location.assign(dest);
  }

  async subscribe() {
    this.error = '';
    this.busy = true;
    try {
      await this.sub.subscribe();   // redirects away
    } catch (e: any) {
      this.busy = false;
      this.error = e?.message || String(e);
    }
  }

  // Open a public, no-login feature tour in its own window. Two audiences: the scientist
  // carousel (index.html) and the plain-language "for the curious" tour (for-you.html).
  openDemo(ev?: Event, page: string = 'index.html') {
    if (ev) ev.preventDefault();
    const safe = page === 'for-you.html' ? 'for-you.html' : 'index.html';
    const win = safe === 'for-you.html' ? 'baja-demo-curious' : 'baja-demo';
    window.open('assets/demo/' + safe, win,
      'width=860,height=820,menubar=no,toolbar=no,location=no,status=no');
  }

  signOut() { this.auth.logout('/login'); }

  /**
   * Take the user into the free-tier editor. A full page navigation, not a router hop:
   * /app/... is resolved by dash.component from the URL, so the app has to load at that
   * path rather than be routed to it in place.
   */
  useFree() { window.location.href = window.location.origin + '/app/free/editor'; }
}
